import {useEffect, useState} from 'react';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';
import {useEventCallback} from 'usehooks-ts';
import {$getSelection, $isRangeSelection} from 'lexical';
import {$patchStyleText, $getSelectionStyleValueForProperty} from '@lexical/selection';
import {mergeRegister} from '@lexical/utils';
import {isRTL} from '../../../Localization';
import {
    FONT_FAMILY_OPTIONS,
    FONT_SIZE,
    FONT_SIZE_OPTIONS,
    LINE_HEIGHT,
    defaultStyles
} from '../constants';
import {addPx} from '../utils';
import {IUseFontDropdown} from '../components/Toolbar.parts/FontDropdown/types';


export const useFontDropdown = ({mode, value, editor}: IUseFontDropdown) => {
    const [selected, setSelected] = useState<DropdownItemData>(value);

    const handleChange = useEventCallback(
        (option: DropdownItemData, isSelected: boolean, name?: string) => {
            if (isSelected) {
                setSelected(isSelected ? option : null);
                handleEditor(option.value as string);
            }
        }
    );

    const handleEditor = (option: string) => {
        const style =
            mode === FONT_SIZE
                ? {
                      [mode]: addPx(option),
                      [LINE_HEIGHT]: addPx(`${parseInt(option, 10) * 1.2}`)
                  }
                : {[mode]: option};

        editor?.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                $patchStyleText(selection, style);
            }
        });
    };

    const isFontSize = mode === FONT_SIZE;

    // Update selected value based on current selection
    useEffect(() => {
        const updateSelectedValue = () => {
            editor.update(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                    if (mode === FONT_SIZE) {
                        // Get font size from selection
                        const fontSize = $getSelectionStyleValueForProperty(
                            selection,
                            FONT_SIZE,
                            addPx(defaultStyles.fontSize)
                        );
                        // Remove 'px' and find matching option
                        const fontSizeValue = fontSize.replace('px', '');
                        const fontSizeOption = FONT_SIZE_OPTIONS.find(
                            option => option.value === fontSizeValue
                        );
                        if (fontSizeOption) {
                            setSelected(fontSizeOption);
                        }
                    } else {
                        // Get font family from selection
                        const fontFamily = $getSelectionStyleValueForProperty(
                            selection,
                            'font-family',
                            defaultStyles.fontFamily,
                        );
                        const fontFamilyOption = FONT_FAMILY_OPTIONS.find(
                            option => option.value === fontFamily
                        );
                        if (fontFamilyOption) {
                            setSelected(fontFamilyOption);
                        }
                    }
                }
            });
        };

        // Register update listener to update selected value when selection changes
        return mergeRegister(
            editor.registerUpdateListener(({editorState}) => {
                editorState.read(() => {
                    updateSelectedValue();
                });
            })
        );
    }, [editor, mode]);

    return {
        handleChange,
        isFontSize,
        selected,
        isRTL: isRTL()
    } as const;
};
